import React from "react";
import { formatWeight, formatMoney } from "@/lib/utils";
import { COLOR_NAME, PURITY_OPTS } from "./mcApi";

const chip = (active) =>
  `relative px-3.5 py-1.5 rounded-full border text-[12px] tracking-[0.04em] transition-colors duration-200 ${
    active ? "bg-[#0D1B2A] text-white border-[#0D1B2A]" : "border-[#0D1B2A]/15 text-[#0D1B2A] hover:border-[#C9A84C] hover:text-[#8a6d1f]"
  }`;

const Tag = () => <span className="ml-1.5 text-[9px] uppercase tracking-[0.14em] text-[#C9A84C]">Uploaded</span>;

// Colour + purity choice. `uploaded` = the design's original variant (tagged and
// the default). `allowUploaded` adds an "As uploaded" chip (global chooser).
export const VariantChooser = ({ color, purity, onChange, testid = "variant", uploaded, allowUploaded, colors = ["Y", "W"] }) => (
  <div className="space-y-2.5" data-testid={`${testid}-chooser`}>
    {[["Colour", "color", colors, (c) => COLOR_NAME[c]], ["Purity", "purity", PURITY_OPTS, (p) => p]].map(([label, field, opts, name]) => (
      <div key={field} className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] uppercase tracking-[0.22em] text-slate-400 w-16">{label}</span>
        {allowUploaded && (
          <button onClick={() => onChange({ color, purity, [field]: null })} className={chip(!(field === "color" ? color : purity))}
            data-testid={`${testid}-${field}-uploaded`}>As uploaded</button>
        )}
        {opts.map((o) => (
          <button key={o} onClick={() => onChange({ color, purity, [field]: o })}
            className={chip((field === "color" ? color : purity) === o)} data-testid={`${testid}-${field}-${o}`}>
            {name(o)}{uploaded && uploaded[field] === o && <Tag />}
          </button>
        ))}
      </div>
    ))}
  </div>
);

export const ReadyDot = ({ testid }) => (
  <span className="inline-flex items-center" title="Ready stock" aria-label="Ready stock" data-testid={testid}>
    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 ring-2 ring-emerald-100" />
  </span>
);

// Refined numerals: lining + tabular figures, open tracking, light currency glyph.
export const Price = ({ v, className = "", testid }) => (
  <span className={`mc-num ${className}`} data-testid={testid}>
    {v == null ? "—" : (<><span className="mc-cur">₹</span>{Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: Number.isInteger(Number(v)) ? 0 : 2 })}</>)}
  </span>
);

// CS 22.14 -- wholesaler links: price-list currency (same formatter as the wholesaler module).
export const Money = ({ v, cur, className = "", testid }) => (
  <span className={`mc-num ${className}`} data-testid={testid}>{v == null ? "—" : formatMoney(v, cur, { decimals: 2 })}</span>
);

export const wt = (v) => (v == null ? "—" : `${formatWeight(v)} g`);
export const ct = (v) => (v == null ? "—" : `${Number(v).toFixed(2)} ct`);

// Contain-fit photo, never cropped and never upscaled past its natural size.
export const Photo = ({ url, size, alt, className = "" }) => (
  <div className={`w-full h-full min-h-0 flex items-center justify-center bg-white overflow-hidden ${className}`}>
    {url ? (
      <img src={url} alt={alt} loading="lazy" className="object-contain max-w-full max-h-full"
        style={size ? { maxWidth: `min(100%, ${size.width}px)`, maxHeight: `min(100%, ${size.height}px)` } : undefined} />
    ) : (
      <span className="text-[10px] uppercase tracking-[0.2em] text-slate-300">No photo</span>
    )}
  </div>
);
